/** Shared formatting helpers — single source of truth for web and mobile. */

/** Formats a dollar amount as USD currency string. */
export function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/**
 * Formats a date as "DD MMM YYYY" (British date format per TCI conventions).
 * Accepts ISO strings, Date objects, null, or undefined.
 * Returns "—" for empty/invalid/null values.
 *
 * Date-only strings ("2026-05-22") are parsed with T00:00:00 appended to
 * prevent UTC midnight from flipping to the previous day in TCI/EST.
 * Full ISO datetimes use UTC to preserve the stated calendar day.
 *
 * D12 (trilingual): pass a BCP-47 `locale` tag (web es/ht) to format via
 * `Intl.DateTimeFormat` for localized month names. en-TC OMITS the arg so the
 * English tables below stay byte-identical (D1), and every mobile call site
 * (no arg) is unchanged until Phase 3 verifies Hermes ICU coverage.
 */
export function formatDate(
  date: string | Date | null | undefined,
  locale?: string,
): string {
  if (date === null || date === undefined || date === "") return "—";
  const isDateOnly = typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const d =
    typeof date === "string"
      ? isDateOnly
        ? new Date(date + "T00:00:00")
        : new Date(date)
      : date;
  if (Number.isNaN(d.getTime())) return "—";
  // Date-only: use local time (we appended T00:00:00 local). Full ISO: use UTC.
  const useLocal = isDateOnly || date instanceof Date;
  if (locale) {
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      ...(useLocal ? {} : { timeZone: "UTC" }),
    }).format(d);
  }
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ] as const;
  const day = useLocal ? d.getDate() : d.getUTCDate();
  const mon = useLocal ? d.getMonth() : d.getUTCMonth();
  const year = useLocal ? d.getFullYear() : d.getUTCFullYear();
  return `${String(day)} ${months[mon] ?? "—"} ${String(year)}`;
}

/**
 * The ISO date (YYYY-MM-DD) a bill is due — the 22nd of the month after the
 * bill month. Split out from `formatBillDueDate` so the "Due" label can be
 * i18n-keyed and the date localized (D12) at the call site, while the legacy
 * string helper below stays byte-identical for mobile (Phase 3).
 */
export function billDueIso(billMonth: string): string {
  const [y, m] = billMonth.split("-").map(Number);
  const dueMonth = (m ?? 0) === 12 ? 1 : (m ?? 0) + 1;
  const dueYear = (m ?? 0) === 12 ? (y ?? 0) + 1 : (y ?? 0);
  return `${String(dueYear)}-${String(dueMonth).padStart(2, "0")}-22`;
}

/**
 * Derives "Due DD MMM YYYY" from a bill month (yyyy-mm).
 * PETCI bills are due on the 22nd of the following month.
 * Retained for mobile (Phase 3); web localizes via `billDueIso` + a keyed label.
 */
export function formatBillDueDate(billMonth: string): string {
  const [y, m] = billMonth.split("-").map(Number);
  const dueMonth = (m ?? 0) === 12 ? 1 : (m ?? 0) + 1;
  const dueYear = (m ?? 0) === 12 ? (y ?? 0) + 1 : (y ?? 0);
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ] as const;
  return `Due 22 ${months[dueMonth - 1] ?? "—"} ${String(dueYear)}`;
}

/** Formats a kWh value with thousands separators. */
export function formatKwh(n: number): string {
  return `${n.toLocaleString("en-US")} kWh`;
}

/**
 * Formats a duration in whole minutes as long-form narrative copy:
 * `160 → "2 hr 40 min"`, `45 → "45 min"`, `120 → "2 hr"`. Used where a
 * duration is derived from a fixture rather than hardcoded (e.g. the P1
 * spoilage insight deriving from `outages[0].durationMinutes`).
 */
export function formatDurationLong(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${String(minutes)} min`;
  if (minutes === 0) return `${String(hours)} hr`;
  return `${String(hours)} hr ${String(minutes)} min`;
}

/** Returns a time-of-day greeting for the given contact name. */
export function greeting(contactName: string, hour?: number): string {
  const h = hour ?? new Date().getHours();
  const part = h < 12 ? "morning" : h < 18 ? "afternoon" : "evening";
  return `Good ${part}, ${contactName}`;
}

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * Formats "YYYY-MM" as "May 2026".
 * Handles YYYY-MM-DD gracefully by extracting the month portion.
 *
 * D12 (trilingual): pass a BCP-47 `locale` tag (web es/ht) for a localized
 * "MMM YYYY" via `Intl`; omitting it keeps the English table byte-identical.
 */
export function formatMonth(yearMonth: string, locale?: string): string {
  if (!yearMonth) return "—";
  const parts = yearMonth.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m || m < 1 || m > 12) return yearMonth;
  if (locale) {
    return new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" })
      .format(new Date(y, m - 1, 1));
  }
  const monthLabel = MONTHS_SHORT[m - 1] ?? "";
  return `${monthLabel} ${String(y)}`;
}

const MONTHS_LONG = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
] as const;

/**
 * Formats "YYYY-MM" as the STANDALONE long month name — "August", no year.
 *
 * Distinct from `formatMonth` ("Aug 2026") because the consumer is a sentence
 * that already carries its own time framing: "Next bill expected early
 * {month}". Injecting a year there ("early Aug 2026") is both redundant and a
 * copy regression on the flagship hero, so this variant drops it (F-W9-1).
 *
 * D12 (trilingual): pass a BCP-47 `locale` tag for the localized standalone
 * form via `Intl` ("agosto"); omitting it keeps the English table
 * byte-identical. Locales without CLDR data (ht) fall back to Intl's own
 * resolution — the documented honest state, same as every other formatter here.
 */
export function formatMonthNameLong(yearMonth: string, locale?: string): string {
  if (!yearMonth) return "—";
  const parts = yearMonth.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  if (!y || !m || m < 1 || m > 12) return yearMonth;
  if (locale) {
    return new Intl.DateTimeFormat(locale, { month: "long" })
      .format(new Date(y, m - 1, 1));
  }
  return MONTHS_LONG[m - 1] ?? "";
}

/**
 * Formats an ETA datetime for outage panels: "Today 16:30",
 * "Tomorrow 09:00", or "21 May 16:30" for dates further out.
 *
 * D12 (trilingual): pass a BCP-47 `locale` tag (web es/ht) to localize the
 * relative day word via `Intl.RelativeTimeFormat` ("Hoy"/"Mañana") and the
 * far-out month via `Intl`. Omitting it keeps the English words byte-identical
 * (D1), so every mobile call site is unchanged until Phase 3 verifies Hermes ICU.
 */
export function formatEta(
  iso: string | null | undefined,
  now?: Date,
  locale?: string,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const reference = now ?? new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  const isToday = d.toDateString() === reference.toDateString();
  const tomorrow = new Date(reference);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  if (locale) {
    if (isToday || isTomorrow) {
      const rel = new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(
        isToday ? 0 : 1,
        "day",
      );
      const cap = rel.charAt(0).toUpperCase() + rel.slice(1);
      return `${cap} ${time}`;
    }
    const md = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(d);
    return `${md} ${time}`;
  }
  if (isToday) return `Today ${time}`;
  if (isTomorrow) return `Tomorrow ${time}`;
  const etaMonth = MONTHS_SHORT[d.getMonth()] ?? "";
  return `${String(d.getDate())} ${etaMonth} ${time}`;
}

/**
 * Formats a storm ETA as the short "Wkd HH:MM" form the storm banner embeds
 * ("Tue 09:00"). D12: pass a BCP-47 `locale` (web es/ht) for the localized
 * weekday via `Intl`; omit it for the byte-identical English table (so the
 * fixture `message` string and every mobile call site are unchanged, Phase 3).
 */
export function formatStormEta(iso: string | null | undefined, locale?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (locale) {
    const wkd = new Intl.DateTimeFormat(locale, { weekday: "short" }).format(d);
    return `${wkd} ${time}`;
  }
  const wkd = WEEKDAYS_SHORT[d.getDay()] ?? "—";
  return `${wkd} ${time}`;
}

/**
 * Returns a short countdown like "2h 14m" or "12m" for an ETA chip.
 * Returns null if the ETA has already passed or is invalid.
 */
export function formatCountdown(iso: string | null | undefined, now?: Date): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - (now ?? new Date()).getTime();
  if (diffMs <= 0) return null;
  const totalMin = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  if (hours === 0) return `${String(minutes)}m`;
  return `${String(hours)}h ${String(minutes)}m`;
}

/**
 * Formats an ETA datetime in the absolute "Wkd D Mon, HH:MM" form the storm
 * banner uses ("Tue 21 Jul, 13:03") — the app's canonical ETA display. Unlike
 * `formatEta` (relative "Today"/"Tomorrow"), it never depends on "now", so it is
 * unambiguous in a scannable list. Uses LOCAL getters to match the storm banner
 * (fixtures' `getDemoStorm` etaDisplay) and the local wall-clock ISO the outage
 * fixtures emit. Returns "—" for empty/invalid — NEVER the raw ISO (N-2).
 *
 * D12 (trilingual): pass a BCP-47 `locale` tag (web es/ht) to localize the
 * weekday + month via `Intl.DateTimeFormat` (local time zone, matching the
 * English-table branch). Omitting it keeps the byte-identical English form
 * "Tue 21 Jul, 13:03" (D1) — so every mobile call site is unchanged (Phase 3).
 */
export function formatEtaLong(
  iso: string | null | undefined,
  locale?: string,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  if (locale) {
    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(d);
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  const wkd = WEEKDAYS_SHORT[d.getDay()] ?? "—";
  const mon = MONTHS_SHORT[d.getMonth()] ?? "—";
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${wkd} ${String(d.getDate())} ${mon}, ${time}`;
}
